import { create } from 'zustand';
import type { TrackPoint } from '@regatta-replay/types';

// ── Types ──

export type TrackVariable =
  | 'sog' | 'vmg' | 'twa' | 'heading' | 'heel' | 'pitch'
  | 'cog' | 'boatSpeed' | 'windDir' | 'windSpeed' | 'depth' | 'waterTemp';

export type FocusVisibility = 'all' | 'allPlusFocus' | 'focusOnly';

export type TrackDisplayMode = 'sog' | 'shortTrack' | 'fullTrack' | 'currentLeg';

export interface TimeBracket {
  start: string;
  end: string;
}

export interface VariableInfo {
  key: TrackVariable;
  label: string;
  unit: string;
  group: 'always' | 'wind' | 'device' | 'sensor';
  available: boolean;
}

const VARIABLE_DEFINITIONS: Omit<VariableInfo, 'available'>[] = [
  { key: 'sog', label: 'Speed Over Ground', unit: 'kts', group: 'always' },
  { key: 'vmg', label: 'Velocity Made Good', unit: 'kts', group: 'wind' },
  { key: 'twa', label: 'True Wind Angle', unit: '°', group: 'wind' },
  { key: 'heading', label: 'Heading', unit: '°', group: 'device' },
  { key: 'heel', label: 'Heel', unit: '°', group: 'device' },
  { key: 'pitch', label: 'Pitch', unit: '°', group: 'device' },
  { key: 'boatSpeed', label: 'Speed Through Water', unit: 'kts', group: 'device' },
  { key: 'cog', label: 'Course Over Ground', unit: '°', group: 'device' },
  { key: 'windDir', label: 'Wind Direction', unit: '°', group: 'sensor' },
  { key: 'windSpeed', label: 'Wind Speed', unit: 'kts', group: 'sensor' },
  { key: 'depth', label: 'Depth', unit: 'm', group: 'sensor' },
  { key: 'waterTemp', label: 'Water Temp', unit: '°C', group: 'sensor' },
];

// ── Computed availability ──

export function computeAvailableVariables(
  tracks: Map<string, TrackPoint[]>
): VariableInfo[] {
  const hasData: Partial<Record<TrackVariable, boolean>> = { sog: true };

  for (const points of tracks.values()) {
    for (const pt of points) {
      if (pt.heading != null) hasData.heading = true;
      if (pt.heel != null) hasData.heel = true;
      if (pt.pitch != null) hasData.pitch = true;
      if (pt.boatSpeed != null) hasData.boatSpeed = true;
      if (pt.cog != null) hasData.cog = true;
      if (pt.windDir != null) hasData.windDir = true;
      if (pt.windSpeed != null) hasData.windSpeed = true;
      if (pt.depth != null) hasData.depth = true;
      if (pt.waterTemp != null) hasData.waterTemp = true;
      if (pt.twd != null) { hasData.vmg = true; hasData.twa = true; }
    }
  }

  return VARIABLE_DEFINITIONS.map((def) => ({
    ...def,
    available: def.group === 'always' || !!hasData[def.key],
  }));
}

export function getUnavailableReason(variable: VariableInfo): string | null {
  if (variable.available) return null;
  switch (variable.group) {
    case 'wind': return 'No wind data available';
    case 'device': return 'No device data available';
    case 'sensor': return 'No sensor data available';
    default: return null;
  }
}

// ── Store ──

interface GraphState {
  // Variable selection
  selectedVariable: TrackVariable;
  availableVariables: VariableInfo[];
  setSelectedVariable: (variable: TrackVariable) => void;
  setAvailableVariables: (variables: VariableInfo[]) => void;

  // Focus
  focusedBoatIds: string[];
  focusVisibility: FocusVisibility;
  focusBoat: (boatId: string) => void;
  unfocusBoat: (boatId: string) => void;
  setFocusVisibility: (mode: FocusVisibility) => void;
  setFocusedBoatIds: (ids: string[]) => void;

  // Time bracket
  timeBracket: TimeBracket | null;
  setTimeBracket: (bracket: TimeBracket | null) => void;

  // Track display
  trackDisplayMode: TrackDisplayMode;
  setTrackDisplayMode: (mode: TrackDisplayMode) => void;

  // Measurement tool
  measurementActive: boolean;
  setMeasurementActive: (active: boolean) => void;

  // Graph dimensions
  graphHeight: number;
  setGraphHeight: (height: number) => void;
}


export const useGraphStore = create<GraphState>()((set, get) => ({
  // Variable selection
  selectedVariable: 'sog',
  availableVariables: VARIABLE_DEFINITIONS.map((def) => ({
    ...def,
    available: def.group === 'always',
  })),
  setSelectedVariable: (variable) => set({ selectedVariable: variable }),
  setAvailableVariables: (variables) => set({ availableVariables: variables }),

  // Focus
  focusedBoatIds: [],
  // Jason 2026-07-04: the graph shows only the focused boats by default.
  focusVisibility: 'focusOnly',
  // Jason 2026-07 (#234): manual focus is UNCAPPED — this store mirrors the
  // viewer IA focus, so a cap here would truncate the mirrored set.
  focusBoat: (boatId) => {
    const { focusedBoatIds } = get();
    if (focusedBoatIds.includes(boatId)) return;
    set({ focusedBoatIds: [...focusedBoatIds, boatId] });
  },
  unfocusBoat: (boatId) => {
    const { focusedBoatIds } = get();
    set({ focusedBoatIds: focusedBoatIds.filter((id) => id !== boatId) });
  },
  setFocusVisibility: (mode) => set({ focusVisibility: mode }),
  setFocusedBoatIds: (ids) => set({ focusedBoatIds: ids }),

  // Time bracket
  timeBracket: null,
  setTimeBracket: (bracket) => set({ timeBracket: bracket }),

  // Track display
  trackDisplayMode: 'fullTrack',
  setTrackDisplayMode: (mode) => set({ trackDisplayMode: mode }),

  // Measurement tool
  measurementActive: false,
  setMeasurementActive: (active) => set({ measurementActive: active }),

  // Graph dimensions
  graphHeight: 120,
  setGraphHeight: (height) => set({ graphHeight: height }),
}));
